<script setup lang="ts">
type PasswordResetForm = {
    email: string
    userEmail: string
}

const { handleSubmit } = useForm<PasswordResetForm>()

const fields: {
    [Property in keyof PasswordResetForm]: any
} = {
    email: useField('email', (value: any) => {
        if (!value) return 'E-Mail-Adresse muss angegeben werden.'
        // einfache E-Mail-Validierung
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!pattern.test(value)) return 'Bitte gib eine gültige E-Mail-Adresse an.'
        return true
    }),
    userEmail: useField('userEmail', () => true) // Honeypot
}

const isRequestSuccessful = ref(false)

const nutzerStore = useNutzerStore()

const submitLoading = computed(() => nutzerStore.isMethodLoading('passwortZuruecksetzenAnfragen'))

const submit = handleSubmit(async () => {
    try {
        await nutzerStore.passwortZuruecksetzenAnfragen({
            userEmail: fields.userEmail.value.value ?? '',
            email: String(fields.email.value?.value)
        })
        isRequestSuccessful.value = true
    } catch (e: unknown) {
        console.error(e)
    }
})
</script>

<template>
    <v-row justify="center" class="mt-6">
        <v-col cols="12" sm="8" md="6" lg="6" xl="4">
            <h1 class="text-center mb-6">Passwort zurücksetzen</h1>

            <v-form @submit.prevent="submit" :disabled="isRequestSuccessful">
                <!-- E-Mail -->
                <v-text-field
                    class="mb-4"
                    v-model.trim="fields.email.value.value"
                    label="E-Mail-Adresse"
                    name="email"
                    data-test="email"
                    :hide-details="!fields.email.errorMessage.value"
                    :error-messages="fields.email.errorMessage.value"
                    required
                    autocomplete="email"
                />

                <!-- Hidden Honeypot -->
                <v-text-field
                    v-model.trim="fields.userEmail.value.value"
                    label="E-Mail-Adresse"
                    name="userEmail"
                    data-test="userEmail"
                    class="d-none"
                    autocomplete="off"
                />

                <v-alert v-if="!isRequestSuccessful" class="w-100 text-muted mb-3">
                    <b>Hinweis:</b>
                    <ul class="mb-0 pl-5">
                        <li>
                            Gib deine E-Mail-Adresse ein. Wenn sie bei uns registriert ist,
                            erhältst du eine Nachricht mit einem Link zum Zurücksetzen deines Passworts.
                        </li>
                        <li>
                            Bitte prüfe auch deinen Spam-Ordner.
                        </li>
                    </ul>
                </v-alert>

                <!-- Buttons -->
                <v-row>
                    <v-col cols="12">
                        <v-btn
                            type="submit"
                            color="#00527e"
                            class="mb-3"
                            block
                            :disabled="nutzerStore.isLoading || isRequestSuccessful"
                            :loading="submitLoading"
                            data-test="Passwort zurücksetzen"
                        >
                            Passwort zurücksetzen anfragen
                        </v-btn>

                        <v-alert
                            v-if="isRequestSuccessful"
                            class="mb-2"
                            icon="mdi-check-circle-outline"
                            type="success"
                            prominent
                        >
                            <b>Anfrage erfolgreich gesendet</b>
                            <ul class="mb-0 pl-5">
                                <li>
                                    Wenn die E-Mail existiert, wurde eine Nachricht zum Zurücksetzen verschickt.
                                </li>
                                <li>
                                    Bitte prüfe auch deinen Spam-Ordner.
                                </li>
                            </ul>
                        </v-alert>

                        <v-btn :to="{ name: 'login' }" variant="plain" color="#00527e" block>
                            Zurück zur Anmeldung
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </v-col>
    </v-row>
</template>
